import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import RoboticMowerCommand from '~/ducks/robotic-mower-command/robotic-mower-command';
import RoboticMowerSetting from '~/ducks/robotic-mower-command/robotic-mower-setting';

export interface ActiveCommand {
  assetId: AssetId;
  command: RoboticMowerCommand | RoboticMowerSetting;
  missionId: number | null;
}

export const initialState: Fields = {
  activeCommands: [],
};

export interface Fields {
  activeCommands: ActiveCommand[];
}

interface SetLoadingStateAction {
  assetId: AssetId;
  loadingState: boolean;
  missionId: number | null;
}

const removeStartParkCommands = (activeCommands, assetId) =>
  activeCommands.filter(
    (activeCommand) =>
      !(activeCommand.assetId === assetId && activeCommand.command in RoboticMowerCommand)
  );

const removeCommandForAssetAndMissionId = (
  activeCommands: ActiveCommand[],
  assetId: AssetId,
  missionId: number | null,
  command: RoboticMowerCommand | RoboticMowerSetting
) => {
  return activeCommands.filter(
    (activeCommand) =>
      !(
        activeCommand.assetId === assetId &&
        (activeCommand.missionId ?? null) === (missionId ?? null) &&
        activeCommand.command === command
      )
  );
};

const reducer = createSlice({
  name: 'robotic-mower-loading-states',
  initialState,
  reducers: {
    setIsStarting: (state, action: PayloadAction<SetLoadingStateAction>) => {
      const { loadingState, missionId, assetId } = action.payload;

      if (!loadingState) {
        state.activeCommands = removeCommandForAssetAndMissionId(
          state.activeCommands,
          assetId,
          missionId,
          RoboticMowerCommand.START
        );
      } else {
        // Remove any active start/park commands
        state.activeCommands = removeStartParkCommands(state.activeCommands, assetId);

        state.activeCommands.push({
          assetId,
          command: RoboticMowerCommand.START,
          missionId,
        });
      }
    },
    setIsParking: (state, action: PayloadAction<SetLoadingStateAction>) => {
      const { loadingState, missionId, assetId } = action.payload;

      if (!loadingState) {
        state.activeCommands = removeCommandForAssetAndMissionId(
          state.activeCommands,
          assetId,
          missionId,
          RoboticMowerCommand.PARK
        );
      } else {
        // Remove any active start/park commands
        state.activeCommands = removeStartParkCommands(state.activeCommands, assetId);

        state.activeCommands.push({
          assetId,
          command: RoboticMowerCommand.PARK,
          missionId,
        });
      }
    },
    setIsSettingPattern: (state, action: PayloadAction<SetLoadingStateAction>) => {
      const { loadingState, missionId, assetId } = action.payload;

      state.activeCommands = removeCommandForAssetAndMissionId(
        state.activeCommands,
        assetId,
        missionId,
        RoboticMowerSetting.PATTERN
      );

      if (loadingState) {
        state.activeCommands.push({
          assetId,
          command: RoboticMowerSetting.PATTERN,
          missionId,
        });
      }
    },
    setIsSettingMissionStatus: (state, action: PayloadAction<SetLoadingStateAction>) => {
      const { loadingState, missionId, assetId } = action.payload;

      state.activeCommands = removeCommandForAssetAndMissionId(
        state.activeCommands,
        assetId,
        missionId,
        RoboticMowerSetting.MISSION_STATUS
      );

      if (loadingState) {
        state.activeCommands.push({
          assetId,
          command: RoboticMowerSetting.MISSION_STATUS,
          missionId,
        });
      }
    },
  },
});

export const {
  setIsStarting,
  setIsParking,
  setIsSettingPattern,
  setIsSettingMissionStatus,
} = reducer.actions;

export default reducer.reducer;
